import { Shader } from "@/core/backends/webgl/shader"

import vertex from "./basic.vert"
import basic from "./basic.frag"
import gamma from "./gamma.frag"
import adjustments from "./adjustments.frag"
import transformCorrectionVertex from "./transform-correction.vert"
import transformCorrection from "./transform-correction.frag"
import displayOutput from "./display-output.frag"
import rgbPlaneOutput from "./rgb-plane-output.frag"
import yuvOutput from "./yuv-output.frag"
import yuvOutputUV from "./yuv-output-uv.frag"
import cropPerspectiveOutput from "./crop-perspective-output.frag"
import viewportVert from "./viewport.vert"
import viewport from "./viewport.frag"
import demosaicHalfInt from "./demosaic-rcd/demosaic-half-int.frag"
import demosaicHalfXTransInt from "./demosaic-xtrans/demosaic-half-xtrans-int.frag"
import raw from "./raw.frag"
import rawInt from "./raw-int.frag"
import rawProfile from "./raw-profile.frag"
import bitmap from "./bitmap.frag"
import guidedDownsample from "./guided-downsample.frag"
import guidedMean from "./guided-mean.frag"
import guidedMul from "./guided-mul.frag"
import guidedComputeA from "./guided-compute-a.frag"
import guidedComputeB from "./guided-compute-b.frag"
import guidedPreprocess from "./guided-preprocess.frag"
import guidedDarkChannel from "./guided-dark-channel.frag"
import refineMaskCombine from "./mask-refine-combine.frag"
import refineMaskPreprocess from "./mask-refine-preprocess.frag"
import refineMaskAB from "./mask-refine-ab.frag"
import gaussianBlur from "./gaussian-blur.frag"

// demosaic full - RCD algorithm
import demosaicRCD1Int from "./demosaic-rcd/demosaic-full-rcd1-int.frag"
import demosaicRCD2 from "./demosaic-rcd/demosaic-full-rcd2.frag"
import demosaicRCD3 from "./demosaic-rcd/demosaic-full-rcd3.frag"
import demosaicRCD4 from "./demosaic-rcd/demosaic-full-rcd4.frag"

// demosaic full - xtrans
import demosaicXTransFastInt from "./demosaic-xtrans/demosaic-xtrans-fast-int.frag"
import demosaicXTransFull1 from "./demosaic-xtrans/demosaic-xtrans-full1.frag"
import demosaicXTransFull2 from "./demosaic-xtrans/demosaic-xtrans-full2.frag"
import demosaicXTransFull3 from "./demosaic-xtrans/demosaic-xtrans-full3.frag"
import demosaicXTransFull4 from "./demosaic-xtrans/demosaic-xtrans-full4.frag"
import demosaicXTransFull5 from "./demosaic-xtrans/demosaic-xtrans-full5.frag"
import demosaicXTransFull6 from "./demosaic-xtrans/demosaic-xtrans-full6.frag"
import demosaicXTransFull7 from "./demosaic-xtrans/demosaic-xtrans-full7.frag"
import demosaicXTransFull8 from "./demosaic-xtrans/demosaic-xtrans-full8.frag"

// wavelet
import waveletPreprocess from "./wavelet-preprocess.frag"
import waveletPostprocess from "./wavelet-postprocess.frag"
import waveletDecomposeSubsample from "./wavelet-decompose-subsample.frag"
import waveletComposeSubsample from "./wavelet-compose-subsample.frag"
import waveletDecomposeHaar from "./wavelet-decompose-haar.frag"
import waveletComposeHaar from "./wavelet-compose-haar.frag"
import waveletDecomposeSubsampleColor from "./wavelet-decompose-subsample-color.frag"
import waveletComposeSubsampleColor from "./wavelet-compose-subsample-color.frag"
import waveletDecomposeHaarColor from "./wavelet-decompose-haar-color.frag"
import waveletComposeHaarColor from "./wavelet-compose-haar-color.frag"
import waveletCombine from "./wavelet-combine.frag"
import waveletSharpen from "./wavelet-sharpen.frag"
import boxBlur from "./wavelet-boxblur.frag"

// Masks
import gradientMask from "./mask-gradient.frag"
import radialMask from "./mask-radial.frag"
import objectMask from "./mask-object.frag"
import brushMask from "./mask-brush.frag"

// Brush mask painting
import brushMaskPainterVert from "./mask-brush-painter.vert"
import brushMaskPainter from "./mask-brush-painter.frag"

// Color sampling
import colorSampler from "./color-sampler.frag"

// Highlights reconstruction
import hiliteReconClipMask from "./hilite-recon-clipmask.frag"
import hiliteReconChrominan from "./hilite-recon-chrominan.frag"
import hiliteReconFill from "./hilite-recon-fill.frag"

// Healing
import fillColor from "./fill-color.frag"
import brushHealingMaskPainterVert from "./mask-healing-painter.vert"
import brushHealingMaskPainter from "./mask-healing-painter.frag"
import healingDiff from "./healing-diff.frag"
import healingPoission from "./healing-poission.frag"
import healingAdd from "./healing-add.frag"
import healingSobel from "./healing-sobel.frag"
import healingCombine from "./healing-combine.frag"
import healingTransformCombine from "./healing-transform-combine.frag"

// AI healing: Prophoto to Jpeg and Ai (Jpeg) to Prophoto
import healingAiToSrgb from "./healing-ai-to-srgb.frag"
import healingAiToProphoto from "./healing-ai-to-prophoto.frag"

// Cropping
import cropPackedYuvFrag from "./crop-packed-yuv.frag"

export function initShaders(gl: WebGL2RenderingContext) {
	return {
		basic: new Shader(gl, vertex, basic, {
			inputTexture: "sampler2D",
		}),

		gamma: new Shader(gl, vertex, gamma, {
			inputTexture: "sampler2D",
		}),

		viewport: new Shader(gl, viewportVert, viewport, {
			viewMatrix: "mat4",
			inputTexture: "sampler2D",
			inputColorSpace: "uint",
			filterNearest: "bool",
		}),

		transformCorrection: new Shader(gl, transformCorrectionVertex, transformCorrection, {
			inputTexture: "sampler2D",

			// Crop + perspective
			cropMatrix: "mat4",
			perspectiveMatrix: "mat4",

			// Lens correction
			renderLensDistortionCorrection: "bool",
			lensDistortionCorrectionFactor: "float",
			lensImageSize: "vec2",
			lensImageCenter: "vec2",
			lensAutoscaleFactor: "float",
			lensNormalizationScale: "float",
			lensDistortionParams: "vec4",
			lensACMDistortionModel: "bool",

			// Vignette correction
			renderLensVignetteCorrection: "bool",
			lensVignetteParams: "vec3",
			lensVignetteCorrectionFactor: "float",

			// Chromatic aberration correction
			renderChromaticAberrationCorrection: "bool",
			lensChromaticAberrationParams: "sampler2D",

			// Manual correction
			lensDistortion: "float",

			// Denoise
			denoiseLumTexture: "sampler2D",
			denoiseColorTexture: "sampler2D",
			renderDenoise: "bool",
			colorNoiseReduction: "float",
			luminanceNoiseReduction: "float",

			// For healing's first step transform, ignore crop matrix and write alphaChannel
			forHealing: "bool",
			// For ai healing's preprocessing, include distortion transform and srgb conversion with tonemapping and exposure adjustment
			forAiHealing: "bool",
			exposure: "float",

			// Histogram
			disableSuperSampling: "bool",
		}),

		displayOutput: new Shader(gl, vertex, displayOutput, {
			inputTexture: "sampler2D",
			alphaTexture: "sampler2D",
			outputColorSpace: "uint",
			showHighlightsClipping: "bool",
			showShadowsClipping: "bool",
			enableDither: "bool",
		}),

		rgbPlaneOutput: new Shader(gl, vertex, rgbPlaneOutput, {
			inputTexture: "sampler2D",
			alphaTexture: "sampler2D",
			outputColorSpace: "uint",
			channel: "int",
		}),

		cropPerspectiveOutput: new Shader(gl, transformCorrectionVertex, cropPerspectiveOutput, {
			inputTexture: "sampler2D",

			// Crop + perspective
			cropMatrix: "mat4",
			perspectiveMatrix: "mat4",
		}),

		yuvOutput: new Shader(gl, vertex, yuvOutput, {
			inputTexture: "sampler2D",
			outputColorSpace: "uint",
			plane: "uint",
		}),

		yuvOutputUV: new Shader(gl, vertex, yuvOutputUV, {
			inputTexture: "sampler2D",
			outputColorSpace: "uint",
		}),

		adjustments: new Shader(gl, vertex, adjustments, {
			inputTexture: "sampler2D",
			adjustmentsCurveTexture: "sampler2D",
			adjustmentsHslTexture: "sampler2D",
			parametricAndContrastCurveTexture: "sampler2D",
			meanA: "sampler2D",
			meanB: "sampler2D",
			grainTexture: "sampler2D",
			userLutTexture: "sampler3D",

			// Curves
			renderCurvesAll: "bool",
			renderCurvesRGB: "bool",
			renderParametricCurve: "bool",

			// Profile
			colorLookTableDims: "vec3",
			colorLookTable: "sampler2D",
			colorProfileType: "uint",
			profileCurveTexture: "sampler2D",
			renderProfileLookTable: "bool",
			profileLookTableDims: "vec3",
			profileLookTable: "sampler2D",
			renderHueSatMap: "bool",
			hueSatMapDims: "vec3",
			hueSatMap: "sampler2D",

			// Local adjustments
			localAdjustmentsTexture: "sampler2D",
			maskTextureArray: "sampler2DArray",
			blurredTexture: "sampler2D",
			wbGainTexture: "sampler2D",
		}),

		raw: new Shader(gl, vertex, raw, {
			offset: "ivec2",
			source: "sampler2D",
			wbCoeffs: "vec3",
			whitePoint: "float",
			blackLevel: "float",
		}),

		rawInt: new Shader(gl, vertex, rawInt, {
			offset: "ivec2",
			source: "sampler2D",
			isMono: "bool",
			wbCoeffs: "vec3",
			whitePoint: "float",
			blackLevel: "float",
		}),

		bitmap: new Shader(gl, vertex, bitmap, {
			source: "sampler2D",
			sourceColorSpace: "uint",
			offset: "ivec2",
			scale: "int",
		}),

		demosaicHalfInt: new Shader(gl, vertex, demosaicHalfInt, {
			offset: "ivec2",
			source: "sampler2D",
			wbCoeffs: "vec3",
			whitePoint: "float",
			blackLevel: "float",
			redMultiplier: "vec4",
			greenMultiplier: "vec4",
			blueMultiplier: "vec4",
		}),

		demosaicHalfXTransInt: new Shader(gl, vertex, demosaicHalfXTransInt, {
			offset: "ivec2",
			source: "sampler2D",
			wbCoeffs: "vec3",
			whitePoint: "float",
			blackLevel: "float",
			redMultiplierA: "mat3",
			greenMultiplierA: "mat3",
			blueMultiplierA: "mat3",
			redMultiplierB: "mat3",
			greenMultiplierB: "mat3",
			blueMultiplierB: "mat3",
		}),

		/**
		 * Texture preprocessing
		 */

		guidedDownsample: new Shader(gl, vertex, guidedDownsample, {
			inputTexture: "sampler2D",
			offset: "vec2",
		}),

		guidedPreprocess: new Shader(gl, vertex, guidedPreprocess, {
			inputTexture: "sampler2D",
			inputDarkChannel: "sampler2D",
		}),

		guidedDarkChannel: new Shader(gl, vertex, guidedDarkChannel, {
			inputTexture: "sampler2D",
			direction: "ivec2",
		}),

		guidedMean: new Shader(gl, vertex, guidedMean, {
			inputTexture: "sampler2D",
			direction: "vec2",
		}),

		guidedMul: new Shader(gl, vertex, guidedMul, {
			inputTexture: "sampler2D",
		}),

		guidedComputeA: new Shader(gl, vertex, guidedComputeA, {
			mean_II: "sampler2D",
			mean_I: "sampler2D",
		}),

		guidedComputeB: new Shader(gl, vertex, guidedComputeB, {
			mean_I: "sampler2D",
			a: "sampler2D",
		}),

		gaussianBlur: new Shader(gl, vertex, gaussianBlur, {
			inputTexture: "sampler2D",
			offset: "vec2",
		}),

		/**
		 * Masks
		 */

		gradientMask: new Shader(gl, transformCorrectionVertex, gradientMask, {
			inputTexture: "sampler2D",
			imageSize: "vec2",
			cropMatrix: "mat4",
			inverted: "bool",
			subtract: "bool",

			startPoint: "vec2",
			endPoint: "vec2",

			channelIndex: "int",
		}),

		radialMask: new Shader(gl, transformCorrectionVertex, radialMask, {
			inputTexture: "sampler2D",
			imageSize: "vec2",
			cropMatrix: "mat4",
			inverted: "bool",
			subtract: "bool",

			center: "vec2",
			radius: "vec2",
			angle: "float",
			feather: "float",

			channelIndex: "int",
		}),

		objectMask: new Shader(gl, transformCorrectionVertex, objectMask, {
			// Crop + perspective
			cropMatrix: "mat4",
			perspectiveMatrix: "mat4",

			// Lens correction
			renderLensDistortionCorrection: "bool",
			lensDistortionCorrectionFactor: "float",
			lensImageSize: "vec2",
			lensImageCenter: "vec2",
			lensAutoscaleFactor: "float",
			lensNormalizationScale: "float",
			lensDistortionParams: "vec4",
			lensACMDistortionModel: "bool",

			// Manual correction
			lensDistortion: "float",

			originalTexture: "sampler2D",
			inputTexture: "sampler2D",
			maskTexture: "sampler2D",
			inverted: "bool",
			subtract: "bool",

			channelIndex: "int",
		}),

		brushMask: new Shader(gl, transformCorrectionVertex, brushMask, {
			// Crop + perspective
			cropMatrix: "mat4",
			perspectiveMatrix: "mat4",

			inputTexture: "sampler2D",
			maskTexture: "sampler2D",
			inverted: "bool",
			subtract: "bool",

			channelIndex: "int",
		}),

		brushMaskPainter: new Shader(gl, brushMaskPainterVert, brushMaskPainter, {
			// To support color aware masking, need transformation (to uncropped space):
			// Perspective
			perspectiveMatrix: "mat4",

			// Lens correction
			renderLensDistortionCorrection: "bool",
			lensDistortionCorrectionFactor: "float",
			lensImageSize: "vec2",
			lensImageCenter: "vec2",
			lensAutoscaleFactor: "float",
			lensNormalizationScale: "float",
			lensDistortionParams: "vec4",
			lensACMDistortionModel: "bool",

			// Manual correction
			lensDistortion: "float",

			inputTexture: "sampler2D",
			brushSize: "float",
			flow: "float",
			feather: "float",
			density: "float",
			erase: "bool",
			colorAware: "bool",
		}),

		/**
		 * Mask refinement
		 */

		refineMaskCombine: new Shader(gl, vertex, refineMaskCombine, {
			inputTexture: "sampler2D",
			inputMask: "sampler2D",
		}),

		refineMaskPreprocess: new Shader(gl, vertex, refineMaskPreprocess, {
			inputTexture: "sampler2D",
			inflation: "bool",
		}),

		refineMaskAB: new Shader(gl, vertex, refineMaskAB, {
			inputTexture: "sampler2D",
			eps: "float",
			computeAB: "bool",
		}),

		/**
		 * Wavelet denoise
		 */

		waveletPreprocess: new Shader(gl, vertex, waveletPreprocess, {
			inputTexture: "sampler2D",
			channel: "int",
			sigma: "int",
			threshold: "float",
			strength: "float",
		}),

		waveletPostprocess: new Shader(gl, vertex, waveletPostprocess, {
			inputLum: "sampler2D",
			threshold: "float",
		}),

		waveletDecomposeSubsample: new Shader(gl, vertex, waveletDecomposeSubsample, {
			inputTexture: "sampler2D",
			isVertical: "bool",
			strength: "float",
		}),

		waveletComposeSubsample: new Shader(gl, vertex, waveletComposeSubsample, {
			inputTexture: "sampler2D",
			isVertical: "bool",
		}),

		waveletDecomposeHaar: new Shader(gl, vertex, waveletDecomposeHaar, {
			inputTexture: "sampler2D",
			step: "int",
			strength: "float",
		}),

		waveletComposeHaar: new Shader(gl, vertex, waveletComposeHaar, {
			inputTexture: "sampler2D",
			originalOutput: "sampler2D",
			step: "int",
		}),

		waveletDecomposeSubsampleColor: new Shader(gl, vertex, waveletDecomposeSubsampleColor, {
			inputTexture: "sampler2D",
			isVertical: "bool",
			strength: "float",
			inWidth: "int",
			inHeight: "int",
			outWidth: "int",
			outHeight: "int",
		}),

		waveletComposeSubsampleColor: new Shader(gl, vertex, waveletComposeSubsampleColor, {
			inputTexture: "sampler2D",
			isVertical: "bool",
			inWidth: "int",
			inHeight: "int",
			outWidth: "int",
			outHeight: "int",
		}),

		waveletDecomposeHaarColor: new Shader(gl, vertex, waveletDecomposeHaarColor, {
			inputTexture: "sampler2D",
			step: "int",
			strength: "float",
			width: "int",
			height: "int",
		}),

		waveletComposeHaarColor: new Shader(gl, vertex, waveletComposeHaarColor, {
			inputTexture: "sampler2D",
			originalOutput: "sampler2D",
			step: "int",
			width: "int",
			height: "int",
		}),

		waveletCombine: new Shader(gl, vertex, waveletCombine, {
			inputTexture: "sampler2D",
			inputChannel: "sampler2D",
			channelToCombine: "int",
		}),

		waveletSharpen: new Shader(gl, vertex, waveletSharpen, {
			denoisedTexture: "sampler2D",
		}),

		boxBlur: new Shader(gl, vertex, boxBlur, {
			inputTexture: "sampler2D",
		}),

		/**
		 * Color sampling
		 */

		colorSampler: new Shader(gl, vertex, colorSampler, {
			// Main sampling
			inputTexture: "sampler2D",
			samplePosition: "vec2",
			sampleSize: "float",
		}),

		/**
		 * Highlights reconstruction
		 */

		hiliteReconClipMask: new Shader(gl, vertex, hiliteReconClipMask, {
			inputTexture: "sampler2D",
			clips: "vec3",
		}),

		hiliteReconChrominan: new Shader(gl, vertex, hiliteReconChrominan, {
			inputTexture: "sampler2D",
			maskTexture: "sampler2D",
			clips: "vec3",
		}),

		hiliteReconFill: new Shader(gl, vertex, hiliteReconFill, {
			inputTexture: "sampler2D",

			wbCoeffs: "vec3",
			clips: "vec3",
			chrominan: "vec3",
			camToXYZ: "mat3",
		}),

		rawProfile: new Shader(gl, vertex, rawProfile, {
			inputTexture: "sampler2D",
			gainMapTexture: "sampler3D",

			baselineExposure: "float",

			gainTableOrigin: "vec2",
			gainTableSpacing: "vec2",
			gainTableSize: "ivec3",
			gainTableWeightsFirst: "vec3", // Split weights into first part...
			gainTableWeightsSecond: "vec2", // ...and the second part
			renderGainTableMap: "bool",
		}),

		/**
		 * Healing
		 */

		fillColor: new Shader(gl, vertex, fillColor, {
			foreColor: "vec4",
		}),

		brushHealingMaskPainter: new Shader(
			gl,
			brushHealingMaskPainterVert,
			brushHealingMaskPainter,
			{
				brushSize: "float",
				flow: "float",
				feather: "float",
				density: "float",
			},
		),

		makeDiff: new Shader(gl, vertex, healingDiff, {
			outputSize: "ivec2",
			destination: "sampler2D",
			source: "sampler2D",
			mask: "sampler2D",
			edgeFlags: "ivec4",
			leftBounds: "vec2",
			rightBounds: "vec2",
			topBounds: "vec2",
			bottomBounds: "vec2",
			isAiFill: "bool",
		}),

		healingPoission: new Shader(gl, vertex, healingPoission, {
			pixels: "sampler2D",
			a: "float",
			w: "float",
			parity: "int",
		}),

		healingAdd: new Shader(gl, vertex, healingAdd, {
			pixels: "sampler2D",
			source: "sampler2D",
		}),

		healingSobel: new Shader(gl, vertex, healingSobel, {
			inputTexture: "sampler2D",
		}),

		healingCombine: new Shader(gl, viewportVert, healingCombine, {
			result: "sampler2D",
			mask: "sampler2D",
			viewMatrix: "mat4",
		}),

		healingTransformCombine: new Shader(
			gl,
			transformCorrectionVertex,
			healingTransformCombine,
			{
				result: "sampler2D",
				mask: "sampler2D",
				destinationRect: "vec4",

				// Crop + perspective
				cropMatrix: "mat4",
				perspectiveMatrix: "mat4",

				// Lens correction
				renderLensDistortionCorrection: "bool",
				lensDistortionCorrectionFactor: "float",
				lensImageSize: "vec2",
				lensImageCenter: "vec2",
				lensAutoscaleFactor: "float",
				lensNormalizationScale: "float",
				lensDistortionParams: "vec4",
				lensACMDistortionModel: "bool",

				// Chromatic aberration correction
				renderChromaticAberrationCorrection: "bool",
				lensChromaticAberrationParams: "sampler2D",

				// Manual correction
				lensDistortion: "float",
			},
		),

		healingAiToProphoto: new Shader(gl, vertex, healingAiToProphoto, {
			source: "sampler2D",
			exposure: "float",
		}),

		healingAiToSrgb: new Shader(gl, vertex, healingAiToSrgb, {
			source: "sampler2D",
			exposure: "float",
		}),

		/**
		 * Utils
		 */

		cropPackedYuv: new Shader(gl, vertex, cropPackedYuvFrag, {
			rect: "vec4",
			yTexture: "sampler2D",
			uTexture: "sampler2D",
			vTexture: "sampler2D",
			outputSize: "ivec2",
			ySize: "ivec2",
			uSize: "ivec2",
		}),

		demosaicRCD1Int: new Shader(gl, vertex, demosaicRCD1Int, {
			source: "sampler2D",
			offset: "vec2",
			wbCoeffs: "vec3",
			blackLevel: "float",
			whitePoint: "float",
			cfa: "vec4",
		}),

		demosaicRCD2: new Shader(gl, vertex, demosaicRCD2, {
			dirTexture: "sampler2D",
			cfa: "vec4",
		}),

		demosaicRCD3: new Shader(gl, vertex, demosaicRCD3, {
			greenTexture: "sampler2D",
			cfa: "vec4",
		}),

		demosaicRCD4: new Shader(gl, vertex, demosaicRCD4, {
			rgbTexture: "sampler2D",
			cfa: "vec4",
		}),

		/**
		 * Demosaic X-Trans
		 */

		demosaicXTransFastInt: new Shader(gl, vertex, demosaicXTransFastInt, {
			source: "sampler2D",
			offset: "vec2",
			wbCoeffs: "vec3",
			blackLevel: "float",
			whitePoint: "float",
			cfa: "intArray",
		}),

		demosaicXTransFull1: new Shader(gl, vertex, demosaicXTransFull1, {
			rawTexture: "sampler2D",
			offset: "vec2",
			wbCoeffs: "vec3",
			blackLevel: "float",
			whitePoint: "float",
			cfa: "intArray",
			allhex: "intArray",
		}),

		demosaicXTransFull2: new Shader(gl, vertex, demosaicXTransFull2, {
			rawTexture: "sampler2D",
			cfa: "intArray",
			allhex: "intArray",
			sgrow: "int",
		}),

		demosaicXTransFull3: new Shader(gl, vertex, demosaicXTransFull3, {
			rawTexture: "sampler2D",
			greenTexture: "sampler2D",
			sgPixel: "ivec2",
			cfa: "intArray",
		}),

		demosaicXTransFull4: new Shader(gl, vertex, demosaicXTransFull4, {
			greenTexture: "sampler2D",
			redTexture: "sampler2D",
			blueTexture: "sampler2D",
			cfa: "intArray",
			sgrow: "int",
		}),

		demosaicXTransFull5: new Shader(gl, vertex, demosaicXTransFull5, {
			greenTexture: "sampler2D",
			redTexture: "sampler2D",
			blueTexture: "sampler2D",
			sgPixel: "ivec2",
			cfa: "intArray",
			allhex: "intArray",
		}),

		demosaicXTransFull6: new Shader(gl, vertex, demosaicXTransFull6, {
			greenTexture: "sampler2D",
			redTexture: "sampler2D",
			blueTexture: "sampler2D",
		}),

		demosaicXTransFull7: new Shader(gl, vertex, demosaicXTransFull7, {
			derivativeTexture: "sampler2D",
		}),

		demosaicXTransFull8: new Shader(gl, vertex, demosaicXTransFull8, {
			greenTexture: "sampler2D",
			redTexture: "sampler2D",
			blueTexture: "sampler2D",
			homogeneityTexture: "sampler2D",
		}),
	}
}
